import { ImageIcon } from "assets/icons";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Progress } from "ui";

import { RevenueByVariant } from "../../../../apis";
import {
  barPercent,
  formatPercent,
  formatXof,
  shareOf
} from "../../financialFormat";
import { CategoryMetric } from "./CategoryBreakdown.types";

import styles from "./CategoryBreakdown.module.scss";

interface ProductRowProps {
  variant: RevenueByVariant;
  metric: CategoryMetric;
  levelTotal: number;
  renderProductExtra?: (
    variant: RevenueByVariant,
    levelTotal: number
  ) => ReactNode;
  renderProductBadge?: (variant: RevenueByVariant) => ReactNode;
}

const ProductRow = ({
  variant,
  metric,
  levelTotal,
  renderProductExtra,
  renderProductBadge
}: ProductRowProps) => {
  const { t } = useTranslation();
  const value = variant[metric];
  const share = shareOf(value, levelTotal);

  return (
    <div className={styles.productRow}>
      <div className={styles.productRow__thumb}>
        {variant.imageUrl ? (
          <img
            className={styles.productRow__image}
            src={variant.imageUrl}
            alt={variant.variantName ?? ""}
            loading="lazy"
          />
        ) : (
          <Icon
            as={ImageIcon}
            boxSize={20}
            className={styles.productRow__imagePlaceholder}
          />
        )}
      </div>

      <div className={styles.productRow__info}>
        <div className={styles.productRow__name}>
          {variant.variantName ?? t("Unknown product")}
        </div>
        <div className={styles.productRow__units}>
          {variant.units} {t("units")}
        </div>
        {renderProductBadge?.(variant)}
      </div>

      <div className={styles.productRow__value}>
        <div className={styles.productRow__amount}>{formatXof(value)}</div>
        {renderProductExtra?.(variant, levelTotal)}
      </div>

      <div className={styles.productRow__share}>
        <div className={styles.productRow__shareBar}>
          <Progress percentage={barPercent(share)} transparentBarBackground />
        </div>
        <span className={styles.productRow__sharePercent}>
          {formatPercent(share, 1)}
        </span>
      </div>
    </div>
  );
};

export default ProductRow;
